import { useEffect, useState } from 'react'
import {
  Box,
  IconButton,
  Typography,
  Avatar,
  Paper,
  Collapse,
} from '@mui/material'
import { Close, Minimize, OpenInFull } from '@mui/icons-material'
import { useTheme, useMediaQuery } from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { chatApi } from '@api/endpoints/chat'
import type { ChatReferenceInput } from '@api/types/chat'
import { useChatStore } from '@store/chatStore'
import { useAuthStore } from '@store/authStore'
import { useGeneralChatConnection } from '@hooks/useSignalR'
import { useFirestoreChat } from '@hooks/useFirestoreChat'
import { ConversationType } from '@api/types/chat'
import { MessageThread } from './MessageThread'
import { MessageInput } from './MessageInput'
import { ConversationSettingsMenu } from './ConversationSettingsMenu'
import { getImageUrl } from '@utils/imageUtils'
import { useNavigate } from 'react-router-dom'

interface ChatPopoutProps {
  conversationId: string
  index: number
}

const POPOUT_WIDTH = 600
const POPOUT_GAP = 16

export function ChatPopout({ conversationId, index }: ChatPopoutProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const { removePopout, setMessages, addMessage, markConversationRead } = useChatStore()
  const [minimized, setMinimized] = useState(false)

  const {
    isConnected,
    sendTypingIndicator,
    onConversationUpdateSignal,
  } = useGeneralChatConnection()

  // Firebase realtime receive for this popout's conversation (replaces the
  // SignalR path when Firebase chat is enabled; the store dedupes by id).
  useFirestoreChat(conversationId, user?.userId)

  // Fetch conversation details
  const { data: conversation } = useQuery({
    queryKey: ['chatConversation', conversationId],
    queryFn: () => chatApi.getConversation(conversationId),
  })

  // Fetch messages
  const { data: messagesData } = useQuery({
    queryKey: ['chatPopoutMessages', conversationId],
    queryFn: () => chatApi.getMessages(conversationId, 1, 50),
    enabled: !!conversationId,
  })

  // Update store when messages load
  useEffect(() => {
    if (messagesData && conversationId) {
      const messages = Array.isArray(messagesData) ? [...messagesData].reverse() : []
      setMessages(conversationId, messages, 1, false)
    }
  }, [messagesData, conversationId, setMessages])

  // Mark as read on mount (MainLayout already handles SignalR room joins globally)
  useEffect(() => {
    if (!conversationId) return

    chatApi.markAsRead(conversationId)
    markConversationRead(conversationId)
    queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
  }, [conversationId, markConversationRead, queryClient])

  // Listen for real-time updates for this conversation
  useEffect(() => {
    if (!isConnected) return

    const unsub = onConversationUpdateSignal?.((signal) => {
      if (signal.conversationId !== conversationId) return
      if (signal.senderId === user?.userId) return

      if (signal.updateType === 'NewMessage') {
        queryClient.invalidateQueries({ queryKey: ['chatPopoutMessages', conversationId] })
        // Mark as read since popout is open
        chatApi.markAsRead(conversationId)
        markConversationRead(conversationId)
        queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
      } else if (signal.updateType === 'MessagesRead' || signal.updateType === 'ReactionChanged' || signal.updateType === 'MessageDeleted') {
        queryClient.invalidateQueries({ queryKey: ['chatPopoutMessages', conversationId] })
      } else if (signal.updateType === 'ParticipantLeft') {
        queryClient.invalidateQueries({ queryKey: ['chatPopoutMessages', conversationId] })
        queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
      }
    })

    return () => { unsub?.() }
  }, [isConnected, onConversationUpdateSignal, conversationId, user?.userId, queryClient, markConversationRead])

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: ({ content, imageId, imageMaxViews, imageViewSeconds, references }: { content: string; imageId?: string; imageMaxViews?: number; imageViewSeconds?: number; references?: ChatReferenceInput[] }) =>
      chatApi.sendMessage(conversationId, { content, imageId, imageMaxViews, imageViewSeconds, references }),
    onSuccess: (message) => {
      addMessage(conversationId, message)
    },
  })

  const handleSendMessage = (content: string, imageId?: string, imageMaxViews?: number, imageViewSeconds?: number, references?: ChatReferenceInput[]) => {
    if (!conversationId) return
    sendMessageMutation.mutate({ content, imageId, imageMaxViews, imageViewSeconds, references })
  }

  const handleImageUpload = async (file: File): Promise<string | undefined> => {
    const response = await chatApi.uploadImage(file)
    return response.imageId
  }

  const handleTyping = (isTyping: boolean) => {
    if (!conversationId) return
    sendTypingIndicator(conversationId, isTyping)
  }

  const handleClose = () => {
    removePopout(conversationId)
  }

  const handleExpand = () => {
    removePopout(conversationId)
    navigate(`/chat?conversation=${conversationId}`)
  }

  // Compute display name
  const otherParticipants = conversation?.participants.filter(
    (p) => p.userId !== user?.userId
  ) || []

  const displayName =
    conversation?.name ||
    otherParticipants.map((p) => p.displayName).join(', ') ||
    'Chat'

  // For a Direct conversation the avatar is the other user's and clicking it opens their profile.
  const directOther =
    conversation?.type === ConversationType.Direct ? otherParticipants[0] : undefined
  const headerAvatarSrc =
    conversation?.type === ConversationType.Group
      ? conversation.groupAvatar || undefined
      : conversation?.type === ConversationType.Adventure
      ? conversation.adventureAvatar || undefined
      : getImageUrl(directOther?.avatarUrl) || undefined
  const handleAvatarClick = (e: React.MouseEvent) => {
    if (!directOther) return
    e.stopPropagation()
    removePopout(conversationId)
    navigate(`/profile/${directOther.userId}`)
  }

  // Position: on desktop, stack from right; on mobile, full width
  const rightOffset = isMobile ? 0 : POPOUT_GAP + index * (POPOUT_WIDTH + POPOUT_GAP)

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        right: isMobile ? 0 : `${rightOffset}px`,
        left: isMobile ? 0 : 'auto',
        width: isMobile ? '100%' : POPOUT_WIDTH,
        height: minimized ? 'auto' : '75vh',
        maxHeight: '75vh',
        zIndex: theme.zIndex.drawer + 2,
        display: isMobile && index > 0 ? 'none' : 'flex',
        flexDirection: 'column',
        borderRadius: minimized ? '12px 12px 0 0' : '12px 12px 0 0',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'primary.contrastText',
          cursor: 'pointer',
          minHeight: 48,
        }}
        onClick={() => setMinimized(!minimized)}
      >
        <Avatar
          src={headerAvatarSrc}
          onClick={handleAvatarClick}
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.8rem',
            cursor: directOther ? 'pointer' : 'default',
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
        <Typography
          variant="subtitle2"
          noWrap
          sx={{ flex: 1, fontWeight: 600, color: 'inherit' }}
        >
          {displayName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <ConversationSettingsMenu
            conversationId={conversationId}
            currentMode={conversation?.notificationMode}
          />
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleExpand()
            }}
            sx={{ color: 'inherit', p: 0.5 }}
          >
            <OpenInFull sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setMinimized(!minimized)
            }}
            sx={{ color: 'inherit', p: 0.5 }}
          >
            <Minimize sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleClose()
            }}
            sx={{ color: 'inherit', p: 0.5 }}
          >
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Collapse in={!minimized} sx={{ flex: minimized ? 0 : 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: 'calc(75vh - 48px)' }}>
          <MessageThread
            conversationId={conversationId}
          />
          <MessageInput
            onSend={handleSendMessage}
            onTyping={handleTyping}
            onImageUpload={handleImageUpload}
            disabled={sendMessageMutation.isPending}
            placeholder="Type a message..."
          />
        </Box>
      </Collapse>
    </Paper>
  )
}
